
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { LineItemMarkupTier } from '@/types/line-item-markup-tiers';
import { calculateCurrentPricing } from '@/utils/pricingCalculations';

export interface PricingPreview {
  old: {
    baseCost: number;
    markupPercentage: number;
    markupSource: string;
    finalPrice: number;
  };
  new: {
    baseCost: number;
    markupPercentage: number;
    markupSource: string;
    finalPrice: number;
  };
}

interface UsePricingPreviewProps {
  lineItemId: string;
  currentBaseCost: number;
  categoryId?: string;
  companyId?: string;
  customTiers?: LineItemMarkupTier[];
  useCustomTiers?: boolean;
}

export const usePricingPreview = ({
  lineItemId,
  currentBaseCost,
  categoryId,
  companyId,
  customTiers = [],
  useCustomTiers = false
}: UsePricingPreviewProps) => {
  const [pricingPreview, setPricingPreview] = useState<PricingPreview | null>(null);

  const generatePricingPreview = async (): Promise<PricingPreview | null> => {
    try {
      // Get current stored pricing data (but never use final_price from DB)
      const { data: currentData, error } = await supabase
        .from('internal_line_items')
        .select('base_cost, markup_percentage, markup_source')
        .eq('id', lineItemId)
        .single();

      if (error) throw error;

      // Calculate OLD final price live (never from DB)
      const oldFinalPrice = (currentData.base_cost || 0) * (1 + (currentData.markup_percentage || 0) / 100);

      // Get NEW pricing from current tiers
      const newPricing = await calculateCurrentPricing(currentBaseCost, categoryId, companyId, customTiers, useCustomTiers);

      return {
        old: {
          baseCost: currentData.base_cost || 0,
          markupPercentage: currentData.markup_percentage || 0,
          markupSource: currentData.markup_source || 'global',
          finalPrice: oldFinalPrice
        },
        new: {
          baseCost: currentBaseCost,
          markupPercentage: newPricing.markup,
          markupSource: newPricing.sourceName,
          finalPrice: newPricing.finalPrice
        }
      };
    } catch (error) {
      console.error('Error generating pricing preview:', error);
      return null;
    }
  };

  const showPricingPreview = async () => {
    const preview = await generatePricingPreview();
    setPricingPreview(preview);
    return preview;
  };

  const closePricingPreview = () => {
    setPricingPreview(null);
  };

  const hasChanges = pricingPreview ? (
    pricingPreview.old.baseCost !== pricingPreview.new.baseCost ||
    pricingPreview.old.markupPercentage !== pricingPreview.new.markupPercentage ||
    pricingPreview.old.markupSource !== pricingPreview.new.markupSource
  ) : false;

  return {
    pricingPreview,
    showPricingPreview,
    closePricingPreview,
    hasChanges
  };
};
